/**
 * @Description Icon wrapper component for MUX v4 migration
 * This component provides backward compatibility for the deprecated Icon component from MUX v3
 * Maps manulifeName props to corresponding CDS icons from @manulife/mux-cds-icons
 * @FileName Icon.jsx
 * @Author MUX v4 Migration
 * @CreatedOn November 11, 2025
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  ButtonPlusFilled,
  ButtonMinusOutlined,
  Search,
  ButtonXFilled,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Info,
  Warning,
  ButtonCheckmarkFilled,
  Calendar,
  ArrowLeft,
  ArrowRight,
  Filter,
  Sort,
  Upload,
  Download,
  Edit,
  Delete,
  Export,
  EarningBarChart,
  Plus1,
  Check,
  ButtonLeftOutlined,
  Checkmark,
  ButtonLeftIcon,
  ButtonRightIcon,
} from '@manulife/mux-cds-icons';

// Map of old manulifeName to new CDS icon components
const iconMap = {
  plus1: Plus1,
  plus: ButtonPlusFilled,
  minus: ButtonMinusOutlined,
  search: Search,
  close: ButtonXFilled,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  info: Info,
  warning: Warning,
  error: Warning,
  success: ButtonCheckmarkFilled,
  // check: ButtonCheckmarkFilled,
  check: Check,
  checkmark: Checkmark,
  calendar: Calendar,
  filter: Filter,
  sort: Sort,
  upload: Upload,
  download: Download,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  arrowleft: ChevronLeft, // Use chevron for pagination (non-circled)
  arrowright: ChevronRight, // Use chevron for pagination (non-circled)
  edit: Edit,
  delete: Delete,
  export: Export,
  earningbarchart: EarningBarChart,
  buttonleftoutlined: ButtonLeftOutlined,
  // Pagination button icons for comparison
  buttonlefticon: ButtonLeftIcon,
  buttonrighticon: ButtonRightIcon,
  EarningBarChart,
};

/**
 * FasatIcon wrapper component
 * @param {Object} props - Component props
 * @param {string} props.manulifeName - Legacy icon name from MUX v3
 * @param {string} props.name - Icon name (supports both name and manulifeName)
 * @param {string} props.fill - Icon color (mapped to 'color' in v4)
 * @param {string} props.color - Icon color
 * @param {string} props.size - Icon size
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.alt - Alt text for accessibility (converted to aria-label for SVG)
 * @returns {JSX.Element} - Icon component
 */
function FasatIcon({ manulifeName, name, fill, color, size, className, alt, ...rest }) {
  // Support both 'name' and 'manulifeName' props
  const iconName = name || manulifeName;

  // Map icon name to CDS icon component (case-insensitive)
  const IconComponent = iconMap[iconName?.toLowerCase()];

  // Convert alt to aria-label for SVG accessibility (SVG elements don't support alt attribute)
  const accessibilityProps = alt ? { 'aria-label': alt, role: 'img' } : {};

  // If no mapping found, return default icon
  if (!IconComponent) {
    return <ButtonPlusFilled color={fill || color} className={className} {...accessibilityProps} {...rest} />;
  }

  // In MUX v3, 'fill' was used, in v4 CDS icons use 'color'
  const iconColor = fill || color;

  return (
    <IconComponent
      color={iconColor}
      size={size}
      className={className}
      {...accessibilityProps}
      {...rest}
    />
  );
}

FasatIcon.propTypes = {
  manulifeName: PropTypes.string,
  name: PropTypes.string,
  fill: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  alt: PropTypes.string,
};

FasatIcon.defaultProps = {
  manulifeName: '',
  name: '',
  fill: undefined,
  color: undefined,
  size: undefined,
  className: '',
  alt: '',
};

export default FasatIcon;
